import React, { useState } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, Target, Activity, Zap, Info, X, BarChart3, Users, DollarSign, Clock } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { useNavigate } from 'react-router-dom';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [hoveredGoal, setHoveredGoal] = useState<string | null>(null);
  const [selectedDataPoint, setSelectedDataPoint] = useState<any>(null);
  const [hoveredKPI, setHoveredKPI] = useState<string | null>(null);
  const [activeChartPoint, setActiveChartPoint] = useState<any>(null);

  const kpiData = [
    { 
      name: 'Jan', 
      value: 85, 
      target: 90,
      signals: [
        { signal: 'Holiday season impact', impact: 'negative', strength: 'medium', category: 'seasonal' },
        { signal: 'New product launch delayed', impact: 'negative', strength: 'high', category: 'operational' }
      ]
    },
    { 
      name: 'Feb', 
      value: 88, 
      target: 90,
      signals: [
        { signal: 'Marketing campaign effectiveness +15%', impact: 'positive', strength: 'high', category: 'marketing' },
        { signal: 'Supply chain disruption', impact: 'negative', strength: 'low', category: 'operational' }
      ]
    },
    { 
      name: 'Mar', 
      value: 92, 
      target: 90,
      signals: [
        { signal: 'Customer retention improved', impact: 'positive', strength: 'high', category: 'customer' },
        { signal: 'Market expansion success', impact: 'positive', strength: 'medium', category: 'market' }
      ]
    },
    { 
      name: 'Apr', 
      value: 89, 
      target: 90,
      signals: [
        { signal: 'Competitor pricing pressure', impact: 'negative', strength: 'medium', category: 'competitive' },
        { signal: 'Product quality issues', impact: 'negative', strength: 'high', category: 'operational' }
      ]
    },
    { 
      name: 'May', 
      value: 94, 
      target: 90,
      signals: [
        { signal: 'AI personalisation rollout', impact: 'positive', strength: 'high', category: 'technology' },
        { signal: 'Customer satisfaction +12%', impact: 'positive', strength: 'high', category: 'customer' }
      ]
    },
    { 
      name: 'Jun', 
      value: 96, 
      target: 90,
      signals: [
        { signal: 'Mobile app redesign success', impact: 'positive', strength: 'high', category: 'technology' },
        { signal: 'Partnership deals closed', impact: 'positive', strength: 'medium', category: 'business' }
      ]
    },
  ];

  const riskData = [
    { 
      name: 'Market Risk', 
      shortName: 'Market',
      value: 35, 
      color: '#EF4444',
      signals: [
        { signal: 'Economic uncertainty rising', impact: 'negative', strength: 'high', category: 'economic', description: 'Global economic indicators showing increased volatility' },
        { signal: 'Consumer spending down 8%', impact: 'negative', strength: 'medium', category: 'market', description: 'Retail sales data indicates reduced consumer confidence' },
        { signal: 'Inflation pressure on costs', impact: 'negative', strength: 'high', category: 'economic', description: 'Supply chain costs increasing due to inflation' }
      ]
    },
    { 
      name: 'Operational Risk', 
      shortName: 'Ops',
      value: 20, 
      color: '#F59E0B',
      signals: [
        { signal: 'Staff turnover increased', impact: 'negative', strength: 'medium', category: 'hr', description: 'Key talent retention challenges in critical departments' },
        { signal: 'System downtime incidents', impact: 'negative', strength: 'low', category: 'technology', description: 'Infrastructure stability issues affecting productivity' },
        { signal: 'Process inefficiencies identified', impact: 'negative', strength: 'medium', category: 'process', description: 'Workflow bottlenecks impacting delivery times' }
      ]
    },
    { 
      name: 'Tech Risk', 
      shortName: 'Tech',
      value: 15, 
      color: '#8B5CF6',
      signals: [
        { signal: 'Legacy system dependencies', impact: 'negative', strength: 'medium', category: 'technology', description: 'Critical systems running on outdated infrastructure' },
        { signal: 'Cybersecurity threats +25%', impact: 'negative', strength: 'high', category: 'security', description: 'Increased attack vectors and security incidents' },
        { signal: 'Technical debt accumulation', impact: 'negative', strength: 'medium', category: 'development', description: 'Code quality issues affecting maintainability' }
      ]
    },
    { 
      name: 'Competitive Risk', 
      shortName: 'Comp',
      value: 30, 
      color: '#06B6D4',
      signals: [
        { signal: 'New market entrant detected', impact: 'negative', strength: 'high', category: 'competitive', description: 'Well-funded startup targeting our key market segment' },
        { signal: 'Price war in key segment', impact: 'negative', strength: 'medium', category: 'pricing', description: 'Competitors aggressively cutting prices to gain market share' },
        { signal: 'Innovation gap widening', impact: 'negative', strength: 'high', category: 'innovation', description: 'Competitors launching advanced features faster' }
      ]
    },
  ];

  const signalData = [
    { 
      name: 'Customer Satisfaction', 
      value: 92, 
      change: 5.2, 
      positive: true,
      detailedSignals: [
        { signal: 'Support response time improved by 40%', impact: 'positive', strength: 'high', category: 'support', timestamp: '2 hours ago' },
        { signal: 'Product quality ratings +15%', impact: 'positive', strength: 'medium', category: 'product', timestamp: '1 day ago' },
        { signal: 'Mobile app crashes reduced 60%', impact: 'positive', strength: 'high', category: 'technology', timestamp: '3 hours ago' }
      ]
    },
    { 
      name: 'Market Share', 
      value: 15.8, 
      change: -1.3, 
      positive: false,
      detailedSignals: [
        { signal: 'Competitor launched similar product', impact: 'negative', strength: 'high', category: 'competitive', timestamp: '1 day ago' },
        { signal: 'Price sensitivity increased', impact: 'negative', strength: 'medium', category: 'pricing', timestamp: '2 days ago' },
        { signal: 'Market saturation in key regions', impact: 'negative', strength: 'medium', category: 'market', timestamp: '1 week ago' }
      ]
    },
    { 
      name: 'Innovation Index', 
      value: 78, 
      change: 8.7, 
      positive: true,
      detailedSignals: [
        { signal: 'R&D investment increased 25%', impact: 'positive', strength: 'high', category: 'investment', timestamp: '1 week ago' },
        { signal: 'Patent applications filed +40%', impact: 'positive', strength: 'medium', category: 'ip', timestamp: '2 weeks ago' },
        { signal: 'Innovation team expanded', impact: 'positive', strength: 'medium', category: 'hr', timestamp: '3 days ago' }
      ]
    },
    { 
      name: 'Operational Efficiency', 
      value: 85, 
      change: 3.1, 
      positive: true,
      detailedSignals: [
        { signal: 'Automation deployment success', impact: 'positive', strength: 'high', category: 'automation', timestamp: '4 hours ago' },
        { signal: 'Process optimisation completed', impact: 'positive', strength: 'medium', category: 'process', timestamp: '2 days ago' },
        { signal: 'Cost reduction initiatives', impact: 'positive', strength: 'low', category: 'cost', timestamp: '1 week ago' }
      ]
    },
  ];

  const alerts = [
    { id: 1, type: 'high', message: 'Customer churn increased by 15% in enterprise segment', time: '2 min ago' },
    { id: 2, type: 'medium', message: 'New competitor product launch detected', time: '1 hour ago' },
    { id: 3, type: 'low', message: 'Quarterly review scheduled for next week', time: '3 hours ago' },
  ];

  // Enhanced goals with signal impact data
  const strategicGoals = [
    {
      name: 'Market Expansion',
      progress: 75,
      target: 80,
      impactingSignals: [
        { signal: 'Customer acquisition cost down 12%', impact: 'positive', strength: 'high', category: 'marketing', timestamp: '1 day ago' },
        { signal: 'Competitor pricing pressure', impact: 'negative', strength: 'medium', category: 'competitive', timestamp: '3 hours ago' },
        { signal: 'Regional market demand +18%', impact: 'positive', strength: 'high', category: 'market', timestamp: '2 days ago' }
      ]
    },
    {
      name: 'Digital Transformation',
      progress: 60,
      target: 75,
      impactingSignals: [
        { signal: 'Cloud migration 85% complete', impact: 'positive', strength: 'high', category: 'technology', timestamp: '1 week ago' },
        { signal: 'Legacy system dependencies', impact: 'negative', strength: 'medium', category: 'technology', timestamp: '2 days ago' },
        { signal: 'Team digital skills gap', impact: 'negative', strength: 'low', category: 'hr', timestamp: '1 week ago' }
      ]
    },
    {
      name: 'Customer Experience',
      progress: 85,
      target: 90,
      impactingSignals: [
        { signal: 'NPS score improved to 72', impact: 'positive', strength: 'high', category: 'customer', timestamp: '6 hours ago' },
        { signal: 'Support response time -25%', impact: 'positive', strength: 'medium', category: 'support', timestamp: '1 day ago' },
        { signal: 'Mobile app crashes +3%', impact: 'negative', strength: 'low', category: 'technology', timestamp: '4 hours ago' }
      ]
    }
  ];

  // Market insights data for clickable cards
  const marketInsights = [
    {
      id: 'emerging-opportunity',
      type: 'opportunity',
      icon: Zap,
      iconColor: 'text-yellow-500',
      title: 'Emerging Opportunity',
      description: 'AI-powered personalisation showing 23% engagement increase',
      details: 'Machine learning algorithms are demonstrating significant improvements in user engagement across all segments.'
    },
    {
      id: 'growth-signal',
      type: 'growth',
      icon: TrendingUp,
      iconColor: 'text-green-500',
      title: 'Growth Signal',
      description: 'Mobile usage up 45% in target demographic',
      details: 'Target demographic showing unprecedented mobile adoption and purchasing behaviour patterns.'
    },
    {
      id: 'market-threat',
      type: 'threat',
      icon: AlertTriangle,
      iconColor: 'text-red-500',
      title: 'Market Threat',
      description: 'Competitor pricing strategy shift detected',
      details: 'Major competitor implementing aggressive pricing strategy that may impact market positioning.'
    }
  ];

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'high': return 'bg-red-900 border-red-600';
      case 'medium': return 'bg-yellow-900 border-yellow-600';
      case 'low': return 'bg-blue-900 border-blue-600';
      default: return 'bg-gray-900 border-gray-600';
    }
  };

  const getProgressColor = (progress: number, target: number) => {
    const ratio = progress / target;
    if (ratio >= 0.9) return 'bg-green-500';
    if (ratio >= 0.7) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getSignalImpactColor = (impact: string, strength: string) => {
    if (impact === 'positive') {
      return strength === 'high' ? 'text-green-400' : strength === 'medium' ? 'text-green-500' : 'text-green-600';
    } else {
      return strength === 'high' ? 'text-red-400' : strength === 'medium' ? 'text-red-500' : 'text-red-600';
    }
  };

  const getSignalIcon = (impact: string) => {
    return impact === 'positive' ? 
      <TrendingUp className="w-3 h-3" /> : 
      <TrendingDown className="w-3 h-3" />;
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'customer': return <Users className="w-4 h-4" />;
      case 'market': return <BarChart3 className="w-4 h-4" />;
      case 'technology': return <Zap className="w-4 h-4" />;
      case 'operational': return <Activity className="w-4 h-4" />;
      case 'pricing': return <DollarSign className="w-4 h-4" />;
      default: return <Info className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'customer': return 'text-blue-400';
      case 'market': return 'text-green-400';
      case 'technology': return 'text-purple-400';
      case 'operational': return 'text-orange-400';
      case 'pricing': return 'text-yellow-400';
      case 'competitive': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const handleChartClick = (data: any, chartType: string) => {
    setSelectedDataPoint({ ...data, chartType });
    setActiveChartPoint(null); // Close any active chart point tooltip
  };

  const handleLineClick = (data: any) => {
    handleChartClick(data, 'Performance Trend');
  };

  const handleMarketInsightClick = (insightId: string) => {
    navigate('/market-insights');
  };

  // Custom tooltip that stays visible and is clickable
  const CustomLineTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-900 border border-gray-600 rounded-lg p-3 lg:p-4 shadow-xl min-w-[200px]">
          <p className="text-white font-medium mb-2">{label}</p>
          <p className="text-indigo-400 mb-1">Value: {payload[0].value}%</p>
          <p className="text-green-400 mb-3">Target: {data.target}%</p>
          
          <div className="mb-3">
            <p className="text-xs text-gray-400 mb-1">Top Signals:</p>
            {data.signals.slice(0, 2).map((signal: any, index: number) => (
              <div key={index} className="flex items-center space-x-1 mb-1">
                <div className={`${getSignalImpactColor(signal.impact, signal.strength)}`}>
                  {getSignalIcon(signal.impact)}
                </div>
                <span className="text-xs text-gray-300 truncate">{signal.signal}</span>
              </div>
            ))}
          </div>
          
          <button
            onClick={() => handleChartClick(data, 'Performance Trend')}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs py-2 px-3 rounded transition-colours"
          >
            View All Signals
          </button>
        </div>
      );
    }
    return null;
  };

  // Custom label function for pie chart - optimised for larger chart
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value, shortName }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.6; // Position labels closer to centre for better fit
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="14"
        fontWeight="700"
        style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}
      >
        <tspan x={x} dy="-0.3em">{shortName}</tspan>
        <tspan x={x} dy="1.2em">{value}%</tspan>
      </text>
    );
  };

  // Enhanced tooltip for pie chart
  const CustomPieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-900 border border-gray-600 rounded-lg p-3 lg:p-4 shadow-xl min-w-[280px]">
          <div className="flex items-center space-x-2 mb-3">
            <div 
              className="w-4 h-4 rounded-full" 
              style={{ backgroundColor: data.color }}
            ></div>
            <h4 className="text-white font-semibold">{data.name}</h4>
            <span className="text-gray-400">({data.value}%)</span>
          </div>
          
          <div className="mb-3">
            <p className="text-xs text-gray-400 mb-2">Key Risk Signals:</p>
            <div className="space-y-2">
              {data.signals.slice(0, 2).map((signal: any, index: number) => (
                <div key={index} className="flex items-start space-x-2">
                  <div className={`mt-0.5 ${getSignalImpactColor(signal.impact, signal.strength)}`}>
                    {getSignalIcon(signal.impact)}
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-300 font-medium">{signal.signal}</p>
                    <p className="text-xs text-gray-500 mt-1">{signal.description}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        signal.strength === 'high' ? 'bg-red-900 text-red-300' : 
                        signal.strength === 'medium' ? 'bg-yellow-900 text-yellow-300' : 
                        'bg-gray-700 text-gray-300'
                      }`}>
                        {signal.strength} risk
                      </span>
                      <span className="text-xs text-gray-500 capitalize">{signal.category}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <button
            onClick={() => handleChartClick(data, 'Risk Distribution')}
            className="w-full bg-red-600 hover:bg-red-700 text-white text-xs py-2 px-3 rounded transition-colours"
          >
            View All Risk Signals ({data.signals.length})
          </button>
        </div>
      );
    }
    return null;
  };

  const SignalDetailModal = ({ dataPoint, onClose }: any) => {
    if (!dataPoint) return null;

    const signals = dataPoint.signals || dataPoint.detailedSignals || dataPoint.impactingSignals || [];

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-700">
            <div>
              <h3 className="text-lg lg:text-xl font-bold text-white">
                Signal Impact Analysis
              </h3>
              <p className="text-gray-400 text-sm lg:text-base">
                {dataPoint.name || `${dataPoint.chartType} - ${dataPoint.name || 'Data Point'}`}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colours"
            >
              <X className="w-5 h-5 lg:w-6 lg:h-6" />
            </button>
          </div>

          <div className="p-4 lg:p-6">
            {dataPoint.value && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:gap-4 mb-4 lg:mb-6">
                <div className="bg-gray-700 rounded-lg p-3">
                  <div className="text-xs text-gray-400">Current Value</div>
                  <div className="text-lg font-bold text-white">{dataPoint.value}%</div>
                </div>
                {dataPoint.target && (
                  <div className="bg-gray-700 rounded-lg p-3">
                    <div className="text-xs text-gray-400">Target</div>
                    <div className="text-lg font-bold text-white">{dataPoint.target}%</div>
                  </div>
                )}
                {dataPoint.change && (
                  <div className="bg-gray-700 rounded-lg p-3">
                    <div className="text-xs text-gray-400">Change</div>
                    <div className={`text-lg font-bold ${dataPoint.positive ? 'text-green-400' : 'text-red-400'}`}>
                      {dataPoint.positive ? '+' : ''}{dataPoint.change}%
                    </div>
                  </div>
                )}
                <div className="bg-gray-700 rounded-lg p-3">
                  <div className="text-xs text-gray-400">Signals</div>
                  <div className="text-lg font-bold text-white">{signals.length}</div>
                </div>
              </div>
            )}

            <div>
              <h4 className="text-base lg:text-lg font-semibold text-white mb-3 lg:mb-4">
                {dataPoint.chartType === 'Risk Distribution' ? 'Risk Signals' : 'Impacting Signals'}
              </h4>
              <div className="space-y-3">
                {signals.map((signal: any, index: number) => (
                  <div key={index} className="bg-gray-700 rounded-lg p-3 lg:p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-start space-x-2 lg:space-x-3">
                        <div className={`mt-1 ${getSignalImpactColor(signal.impact, signal.strength)}`}>
                          {getSignalIcon(signal.impact)}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-white font-medium">{signal.signal}</p>
                          {signal.description && (
                            <p className="text-xs text-gray-400 mt-1">{signal.description}</p>
                          )}
                          <div className="flex flex-wrap items-center gap-2 lg:gap-3 mt-2">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              signal.impact === 'positive' ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'
                            }`}>
                              {signal.impact}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              signal.strength === 'high' ? 'bg-red-900 text-red-300' : 
                              signal.strength === 'medium' ? 'bg-yellow-900 text-yellow-300' : 
                              'bg-gray-600 text-gray-300'
                            }`}>
                              {signal.strength} {dataPoint.chartType === 'Risk Distribution' ? 'risk' : 'impact'}
                            </span>
                            {signal.timestamp && (
                              <div className="flex items-center space-x-1 text-xs text-gray-500">
                                <Clock className="w-3 h-3" />
                                <span>{signal.timestamp}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1 flex-shrink-0">
                        <div className={getCategoryColor(signal.category)}>
                          {getCategoryIcon(signal.category)}
                        </div>
                        <span className="text-xs text-gray-400 capitalize hidden sm:inline">{signal.category}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">Strategic Dashboard</h1>
          <p className="text-gray-400 text-sm lg:text-base">Real-time monitoring of strategic goals and market signals</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 mt-4 lg:mt-0">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-300">Live Data Feed</span>
          </div>
          <div className="text-sm text-gray-400">Last updated: 2 min ago</div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {signalData.map((signal, index) => (
          <div 
            key={index} 
            className="bg-gray-800 rounded-lg p-4 lg:p-6 border border-gray-700 hover:border-gray-600 transition-colours cursor-pointer relative"
            onMouseEnter={() => setHoveredKPI(signal.name)}
            onMouseLeave={() => setHoveredKPI(null)}
            onClick={() => setSelectedDataPoint({ ...signal, chartType: 'KPI' })}
          >
            <div className="flex items-center justify-between mb-3 lg:mb-4">
              <h3 className="text-sm font-medium text-gray-300">{signal.name}</h3>
              {signal.positive ? (
                <TrendingUp className="w-4 h-4 lg:w-5 lg:h-5 text-green-500" />
              ) : (
                <TrendingDown className="w-4 h-4 lg:w-5 lg:h-5 text-red-500" />
              )}
            </div>
            <div className="flex items-baseline space-x-2">
              <span className="text-xl lg:text-2xl font-bold text-white">{signal.value}</span>
              <span className="text-sm text-gray-400">%</span>
            </div>
            <div className="flex items-center mt-2">
              <span className={`text-sm ${signal.positive ? 'text-green-500' : 'text-red-500'}`}>
                {signal.positive ? '+' : ''}{signal.change}%
              </span>
              <span className="text-sm text-gray-400 ml-1">vs last month</span>
            </div>
            
            {/* Quick Signal Preview on Hover */}
            {hoveredKPI === signal.name && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-gray-900 border border-gray-600 rounded-lg p-3 z-10 shadow-xl">
                <div className="text-xs text-gray-400 mb-2">Top Signals ({signal.detailedSignals.length})</div>
                <div className="space-y-1">
                  {signal.detailedSignals.slice(0, 2).map((s, i) => (
                    <div key={i} className="flex items-center space-x-2">
                      <div className={`${getSignalImpactColor(s.impact, s.strength)}`}>
                        {getSignalIcon(s.impact)}
                      </div>
                      <span className="text-xs text-gray-300 truncate">{s.signal}</span>
                    </div>
                  ))}
                </div>
                <div className="text-xs text-indigo-400 mt-2">Click for full analysis</div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Performance Trend */}
        <div className="bg-gray-800 rounded-lg p-4 lg:p-6 border border-gray-700">
          <h3 className="text-base lg:text-lg font-semibold text-white mb-1">Performance vs Target</h3>
          <p className="text-xs text-gray-400 mb-3 lg:mb-4">Click data points for signal details</p>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={kpiData} onClick={handleLineClick}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="name" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip 
                content={<CustomLineTooltip />}
                position={{ x: 0, y: 0 }}
                allowEscapeViewBox={{ x: false, y: true }}
                wrapperStyle={{ zIndex: 1000 }}
              />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke="#4F46E5" 
                strokeWidth={3}
                dot={{ fill: '#4F46E5', strokeWidth: 2, r: 6, cursor: 'pointer' }}
                activeDot={{ r: 8, stroke: '#4F46E5', strokeWidth: 2, fill: '#1F2937', cursor: 'pointer' }}
              />
              <Line 
                type="monotone" 
                dataKey="target" 
                stroke="#10B981" 
                strokeWidth={2} 
                strokeDasharray="5 5"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Risk Distribution - Increased Size */}
        <div className="bg-gray-800 rounded-lg p-4 lg:p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-3 lg:mb-4">
            <h3 className="text-base lg:text-lg font-semibold text-white">Risk Distribution</h3>
            <div className="text-xs text-gray-400">Hover segments for risk details</div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={riskData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomLabel}
                outerRadius={120}
                innerRadius={30}
                dataKey="value"
                onClick={(data) => handleChartClick(data, 'Risk Distribution')}
                style={{ cursor: 'pointer' }}
              >
                {riskData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                content={<CustomPieTooltip />}
                wrapperStyle={{ zIndex: 1000 }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Alerts and Goals */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Risk Alerts */}
        <div className="bg-gray-800 rounded-lg p-4 lg:p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-3 lg:mb-4">
            <h3 className="text-base lg:text-lg font-semibold text-white">Risk Alerts</h3>
            <AlertTriangle className="w-4 h-4 lg:w-5 lg:h-5 text-yellow-500" />
          </div>
          <div className="space-y-3">
            {alerts.map((alert) => (
              <div key={alert.id} className={`p-3 lg:p-4 rounded-lg border ${getAlertColor(alert.type)}`}>
                <p className="text-sm text-white mb-2">{alert.message}</p>
                <p className="text-xs text-gray-400">{alert.time}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Strategic Goals with Signal Impact */}
        <div className="bg-gray-800 rounded-lg p-4 lg:p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-3 lg:mb-4">
            <h3 className="text-base lg:text-lg font-semibold text-white">Strategic Goals</h3>
            <div className="flex items-center space-x-2">
              <Target className="w-4 h-4 lg:w-5 lg:h-5 text-indigo-500" />
              <Info className="w-3 h-3 lg:w-4 lg:h-4 text-gray-400" />
              <span className="text-xs text-gray-400 hidden sm:inline">Hover for signal impact</span>
            </div>
          </div>
          <div className="space-y-4">
            {strategicGoals.map((goal, index) => (
              <div 
                key={index}
                className="relative cursor-pointer"
                onMouseEnter={() => setHoveredGoal(goal.name)}
                onMouseLeave={() => setHoveredGoal(null)}
                onClick={() => setSelectedDataPoint({ ...goal, chartType: 'Strategic Goal' })}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-300">{goal.name}</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-400">{goal.progress}% / {goal.target}%</span>
                    <div className="w-16 lg:w-20 bg-gray-700 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(goal.progress, goal.target)}`}
                        style={{ width: `${(goal.progress / goal.target) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Signal Impact Tooltip */}
                {hoveredGoal === goal.name && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-gray-900 border border-gray-600 rounded-lg p-3 lg:p-4 z-10 shadow-xl">
                    <h4 className="text-sm font-medium text-white mb-3">Impacting Signals</h4>
                    <div className="space-y-2">
                      {goal.impactingSignals.slice(0, 2).map((signalItem, signalIndex) => (
                        <div key={signalIndex} className="flex items-start space-x-2">
                          <div className={`mt-0.5 ${getSignalImpactColor(signalItem.impact, signalItem.strength)}`}>
                            {getSignalIcon(signalItem.impact)}
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-gray-300">{signalItem.signal}</p>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                signalItem.impact === 'positive' ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'
                              }`}>
                                {signalItem.impact}
                              </span>
                              <span className="text-xs text-gray-500">{signalItem.strength} impact</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="text-xs text-indigo-400 mt-2">Click for full analysis</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Market Insights - Now Clickable */}
      <div className="bg-gray-800 rounded-lg p-4 lg:p-6 border border-gray-700">
        <div className="flex items-center justify-between mb-3 lg:mb-4">
          <h3 className="text-base lg:text-lg font-semibold text-white">Market Insights & Opportunities</h3>
          <div className="flex items-center space-x-2">
            <Activity className="w-4 h-4 lg:w-5 lg:h-5 text-green-500" />
            <button
              onClick={() => navigate('/market-insights')}
              className="text-xs text-indigo-400 hover:text-indigo-300 transition-colours"
            >
              View All Insights →
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 lg:gap-4">
          {marketInsights.map((insight) => (
            <div 
              key={insight.id}
              onClick={() => handleMarketInsightClick(insight.id)}
              className="bg-gray-700 rounded-lg p-3 lg:p-4 cursor-pointer hover:bg-gray-600 transition-colours group"
            >
              <div className="flex items-center space-x-2 mb-2">
                <insight.icon className={`w-4 h-4 ${insight.iconColor}`} />
                <span className="text-sm font-medium text-white group-hover:text-indigo-300 transition-colours">
                  {insight.title}
                </span>
              </div>
              <p className="text-sm text-gray-300 mb-2">{insight.description}</p>
              <p className="text-xs text-gray-400">{insight.details}</p>
              <div className="mt-3 text-xs text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity">
                Click to explore detailed insights →
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Signal Detail Modal */}
      <SignalDetailModal 
        dataPoint={selectedDataPoint} 
        onClose={() => setSelectedDataPoint(null)} 
      />
    </div>
  );
};

export default Dashboard;